// function/eeg_generator.js

// --- small DSP helpers (biquad filters from RBJ cookbook) ---
function makeBiquadLowpass(fs, f0, Q = 0.707) {
    const w0 = (2 * Math.PI * f0) / fs;
    const cosw0 = Math.cos(w0);
    const sinw0 = Math.sin(w0);
    const alpha = sinw0 / (2 * Q);
  
    let b0 = (1 - cosw0) / 2;
    let b1 = 1 - cosw0;
    let b2 = (1 - cosw0) / 2;
    let a0 = 1 + alpha;
    let a1 = -2 * cosw0;
    let a2 = 1 - alpha;
  
    // normalize
    b0 /= a0; b1 /= a0; b2 /= a0; a1 /= a0; a2 /= a0;
  
    // DF2T state
    let z1 = 0, z2 = 0;
    return {
      process(x) {
        const y = b0 * x + z1;
        z1 = b1 * x - a1 * y + z2;
        z2 = b2 * x - a2 * y;
        return y;
      },
    };
  }
  
  function makeBiquadHighpass(fs, f0, Q = 0.707) {
    const w0 = (2 * Math.PI * f0) / fs;
    const cosw0 = Math.cos(w0);
    const sinw0 = Math.sin(w0);
    const alpha = sinw0 / (2 * Q);
  
    let b0 = (1 + cosw0) / 2;
    let b1 = -(1 + cosw0);
    let b2 = (1 + cosw0) / 2;
    let a0 = 1 + alpha;
    let a1 = -2 * cosw0;
    let a2 = 1 - alpha;
  
    // normalize
    b0 /= a0; b1 /= a0; b2 /= a0; a1 /= a0; a2 /= a0;
  
    let z1 = 0, z2 = 0;
    return {
      process(x) {
        const y = b0 * x + z1;
        z1 = b1 * x - a1 * y + z2;
        z2 = b2 * x - a2 * y;
        return y;
      },
    };
  }
  
  function randn() {
    // Box–Muller
    let u = 0, v = 0;
    while (u === 0) u = Math.random();
    while (v === 0) v = Math.random();
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  }
  
  function clamp(v, lo, hi) {
    return Math.max(lo, Math.min(hi, v));
  }
  
  // --- EEG-ish stream with "monitor sweep" display buffer ---
  // - underlying signal continues (t keeps increasing)
  // - display sweeps left->right for sweepSeconds, then clears and starts again
  export function makeEEGGenerator({ fs = 256, sweepSeconds = 4 } = {}) {
    let t = 0;
  
    // bandpass ~0.5–40 Hz via HP then LP
    const hp = makeBiquadHighpass(fs, 0.5);
    const lp = makeBiquadLowpass(fs, 40);
  
    // alpha burst envelope (comes/goes)
    let alphaAmp = 12;
    let alphaTarget = 12;
    let alphaPhase = Math.random() * 2 * Math.PI;
  
    // EMG burst
    let emgLeft = 0;
  
    // sweep buffer
    const sweepSamples = Math.max(8, Math.floor(fs * sweepSeconds));
    const sweep = new Array(sweepSamples).fill(null);
    let sweepIndex = 0;
  
    const nextSample = () => {
      const dt = 1 / fs;
      t += dt;
  
      // slowly vary alpha presence
      if (Math.random() < 0.01) alphaTarget = 6 + Math.random() * 26; // 6..32
      alphaAmp += (alphaTarget - alphaAmp) * 0.01;
      alphaAmp = clamp(alphaAmp, 0, 35);
  
      // base: band-limited noise (EEG "texture")
      let x = randn() * 22;
      x = lp.process(hp.process(x));
  
      // alpha rhythm with slight frequency wobble
      const alphaHz = 10 + Math.sin(t * 0.15) * 1.0;
      alphaPhase += 2 * Math.PI * alphaHz * dt;
      const alpha = Math.sin(alphaPhase) * alphaAmp;
  
      // small theta
      const theta = Math.sin(2 * Math.PI * 6 * t) * 3.5;
  
      // occasional short EMG fuzz
      if (emgLeft <= 0 && Math.random() < 0.002) emgLeft = Math.floor(fs * 0.18);
      let emg = 0;
      if (emgLeft > 0) {
        emg = randn() * 10;
        emgLeft -= 1;
      }
  
      // combine + clamp to your chart range
      let y = x + alpha + theta + emg;
      y = clamp(y, -120, 120);
  
      return y;
    };
  
    // advance N samples and update sweep buffer
    const step = (n = 1) => {
      const steps = Math.max(1, n | 0);
  
      for (let i = 0; i < steps; i++) {
        // when we wrap, clear buffer to emulate monitor reset
        if (sweepIndex === 0) sweep.fill(null);
  
        sweep[sweepIndex] = nextSample();
        sweepIndex = (sweepIndex + 1) % sweepSamples;
      }
  
      return sweep;
    };
  
    return {
      fs,
      sweepSeconds,
      sweepSamples,
  
      // returns the sweep array (contains numbers + nulls)
      step,
  
      // optional: where the "pen" is
      getSweepIndex() {
        return sweepIndex;
      },
    };
  }
  